/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import useRefInnerText from 'bundles/common/hooks/useRefInnerText';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { BannerAdProps } from 'bundles/promo-units/components/banner-ad';
import { BACKGROUND_COLORS } from 'bundles/promo-units/components/banner-ad';
import LinkButton, { navigateOnClick, openInNewWindow } from 'bundles/promo-units/components/banner-ad/LinkButton';
import { styles } from 'bundles/promo-units/components/banner-ad/__styles__/BannerAd';
import { DEFFAULT_IMAGE_SIZE, aspectRatio } from 'bundles/promo-units/components/banner-ad/constants';
import { makeOptimizedImageUrl, numToPx } from 'bundles/promo-units/components/banner-ad/utils';

import _t from 'i18n!nls/promo-units';

// Why "Soapbox?"
// The CSS name "banner-ad" triggers ad blockers.
// Why two containers, inner and outer?
// TrackedDiv injects an extra div which above itself
// when withVisibilityTracking is set!
// And that div gets no styling / cannot be styled!

const BannerAd = (props: BannerAdProps) => {
  const {
    trackingName = 'promo_unit',
    className,
    callToActionText = _t('Enroll now'),
    callToActionUrl,
    heightInPixels,
    imageUrl,
    imageAltText = '',
    shouldDisplayImageResponsively = false,
    partnerName,
    bodyText,
    imageWidthInPixels = DEFFAULT_IMAGE_SIZE.width,
    backgroundColor = BACKGROUND_COLORS.white,
    style,
    titleText,
    trackingData,
    forceOpenInNewTab,
    ctaButtonType,
    logoUrl,
    logoAltText = '',
  } = props;

  const [titleRef, partnerNameAsString] = useRefInnerText<HTMLDivElement>(
    typeof partnerName === 'string' ? partnerName : ''
  );

  const ctaStyles = logoUrl ? styles.ctaWrapper : '';

  let imageStyle;
  let containerStyle = style;

  const imageWidth = heightInPixels ? aspectRatio * heightInPixels : imageWidthInPixels;
  const optimizedImageUrl = makeOptimizedImageUrl(imageUrl, imageWidth);

  if (heightInPixels) {
    imageStyle = css`
      height: ${numToPx(heightInPixels)};
      width: ${numToPx(imageWidth)};
    `;
    containerStyle = {
      maxHeight: numToPx(heightInPixels),
      ...style,
    };
  }

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => navigateOnClick(event, callToActionUrl);

  const onClickNewWindowHandler = (event: React.MouseEvent<HTMLButtonElement>) =>
    openInNewWindow(event, callToActionUrl);

  const ariaLabel = `${partnerNameAsString} ${titleText ?? ''}`;

  // v3 eventing
  const trackV3 = useTracker();
  const v3VisibilityTracker: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: { moduleType: 'promo_unit' },
    }
  );

  const handleCtaClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    trackV3('click_merchandising_module', {
      merchandisingModule: { moduleType: 'promo_unit' },
      button: {
        name: 'promo_unit_cta',
        linkURL: callToActionUrl,
        linkType: 'internal',
      },
    });
  };

  const handleBannerClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    trackV3('click_merchandising_module', {
      merchandisingModule: { moduleType: 'promo_unit' },
      button: {
        name: 'merchandising_module',
        linkURL: callToActionUrl,
        linkType: 'internal',
      },
    });

    if (forceOpenInNewTab) {
      onClickNewWindowHandler(e);
    } else {
      onClickHandler(e);
    }
  };

  // END - v3 eventing

  // Why two containers, inner and outer?
  // TrackedDiv injects an extra div which above itself
  // when withVisibilityTracking is set!
  // And that div gets no styling / cannot be styled!

  return (
    <div css={styles.bannerAdOuterContainer} className={className}>
      <TrackedDiv
        trackingName={trackingName}
        data={trackingData}
        withVisibilityTracking
        css={[
          styles.bannerAdInnerContainer,
          shouldDisplayImageResponsively && styles.bannerAdInnerContainerResponsiveImage,
        ]}
        className={backgroundColor}
        data-testid="banner-ad"
        style={containerStyle}
        trackClicks
        role="link"
        onClick={handleBannerClick}
        aria-label={ariaLabel}
      >
        <div ref={v3VisibilityTracker} css={styles.leftSideContainer}>
          <div>
            <Typography2 variant="subtitleMedium" component="div" ref={titleRef} css={styles.partner}>
              {partnerName}
            </Typography2>
            <Typography variant="h2semibold" component="h3" css={styles.title}>
              <a href={callToActionUrl} tabIndex={-1} aria-hidden={true}>
                {titleText}
              </a>
            </Typography>
          </div>
          {typeof bodyText === 'string' ? (
            <Typography2 variant="bodySecondary" component="p" css={styles.bodyText}>
              {bodyText}
            </Typography2>
          ) : (
            <React.Fragment>{bodyText}</React.Fragment>
          )}
          <div css={ctaStyles}>
            <LinkButton
              trackingData={trackingData}
              targetUrl={callToActionUrl}
              trackingName={trackingName}
              variant={ctaButtonType ?? 'secondary'}
              size="small"
              label={callToActionText}
              tabIndex={-1}
              forceOpenInNewTab={forceOpenInNewTab}
              onClick={handleCtaClick}
              ariaHidden
            />
            {logoUrl && <img src={logoUrl} alt={logoAltText} className="logo" aria-hidden />}
          </div>
        </div>
        <div className="right-side" css={[styles.rightSideContainer, imageStyle]}>
          <img src={optimizedImageUrl} alt={imageAltText} aria-hidden css={imageStyle} />
        </div>
      </TrackedDiv>
    </div>
  );
};

export default BannerAd;
